import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAdminLocale, t } from '../i18n';
import MailboxPanel from '../components/MailboxPanel';
import {
  createEmailCampaign,
  fetchEmailCampaigns,
  fetchEmailDeliveries,
  fetchEmailSummary,
  searchEmailRecipients,
} from '../api/emailApi';

const EMPTY_FORM = {
  subject: '',
  title: '',
  body_text: '',
  button_label: '',
  button_url: '',
  footer_text: t('본 메일은 Night Poker Club에서 발송되었습니다.'),
};

const STATUS_LABELS = {
  QUEUING: '대상 등록 중',
  QUEUED: '발송 대기',
  SENDING: '발송 중',
  COMPLETED: '완료',
  PARTIAL_FAILED: '일부 실패',
  FAILED: '실패',
};

const TARGET_LABELS = {
  TEST: '테스트 발송',
  INDIVIDUAL: '개별 유저',
  MARKETING_CONSENT: '수신 동의 전체',
};

const DELIVERY_STATUS_LABELS = {
  QUEUED: '발송 대기',
  SENDING: '발송 중',
  SENT: '완료',
  COMPLETED: '완료',
  RETRY_WAIT: '재시도 대기',
  FAILED: '실패',
};

function formatNumber(value) {
  return Number(value || 0).toLocaleString(getAdminLocale());
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(getAdminLocale());
}

function statusClass(status) {
  if (status === 'COMPLETED') return 'bg-emerald-100 text-emerald-700';
  if (status === 'FAILED') return 'bg-red-100 text-red-700';
  if (status === 'PARTIAL_FAILED') return 'bg-amber-100 text-amber-700';
  if (status === 'SENDING') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-700';
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(value)}</div>
      {note ? <div className="mt-1 text-xs text-gray-400">{note}</div> : null}
    </div>
  );
}

function EmailPreview({ form }) {
  const title = form.title.trim() || form.subject.trim() || t('이메일 제목 미리보기');
  const body = form.body_text.trim() || t('작성한 이메일 본문이 여기에 표시됩니다.');
  return (
    <div className="overflow-hidden rounded-xl border border-gray-700 bg-[#0b0b0d] p-4 shadow-sm sm:p-6">
      <div className="mx-auto max-w-[620px] rounded-lg border border-[#303038] bg-[#151519] p-6 text-white">
        <div className="mb-3 text-xs tracking-widest text-[#ffb347]">NIGHT POKER CLUB</div>
        <h3 className="mb-5 text-xl font-bold">{title}</h3>
        <div className="whitespace-pre-wrap break-words text-sm leading-7 text-[#e7e7ea]">{body}</div>
        {form.button_label.trim() && form.button_url.trim() ? (
          <div className="my-6">
            <span className="inline-block rounded-md bg-[#ff9f1c] px-5 py-3 text-sm font-extrabold text-[#111]">
              {form.button_label}
            </span>
          </div>
        ) : null}
        {form.button_url.trim() ? (
          <div className="break-all text-[11px] leading-5 text-gray-400">{form.button_url}</div>
        ) : null}
        {form.footer_text.trim() ? (
          <div className="mt-6 whitespace-pre-wrap text-xs leading-6 text-gray-400">{form.footer_text}</div>
        ) : null}
      </div>
    </div>
  );
}

export default function EmailManagement() {
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [targetType, setTargetType] = useState('TEST');
  const [testEmail, setTestEmail] = useState('');
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [viewMode, setViewMode] = useState('mailbox');

  const loadDashboard = useCallback(async () => {
    const [summaryData, campaignData] = await Promise.all([
      fetchEmailSummary(),
      fetchEmailCampaigns({ page: 1, page_size: 30 }),
    ]);
    setSummary(summaryData);
    setCampaigns(Array.isArray(campaignData?.campaigns) ? campaignData.campaigns : []);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        await loadDashboard();
      } catch (error) {
        if (alive) alert(error?.response?.data?.message || error.message || t('이메일 관리 정보를 불러오지 못했습니다.'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [loadDashboard]);

  const recipientEstimate = useMemo(() => {
    if (targetType === 'TEST') return testEmail.trim() ? 1 : 0;
    if (targetType === 'INDIVIDUAL') return selectedRecipient ? 1 : 0;
    return Number(summary?.marketing_recipient_count || 0);
  }, [targetType, testEmail, selectedRecipient, summary]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleRecipientSearch = async () => {
    const q = recipientQuery.trim();
    if (!q) return;
    setSearching(true);
    try {
      const rows = await searchEmailRecipients(q);
      setRecipientResults(rows);
      setSelectedRecipient((current) => rows.find((row) => row.user_code === current?.user_code) || null);
    } catch (error) {
      alert(error?.response?.data?.message || error.message || t('유저 검색에 실패했습니다.'));
    } finally {
      setSearching(false);
    }
  };

  const handleSend = async () => {
    if (!form.subject.trim() || !form.body_text.trim()) {
      alert(t('메일 제목과 본문을 입력해 주세요.'));
      return;
    }
    if (targetType === 'TEST' && !testEmail.trim()) {
      alert(t('테스트 수신 이메일을 입력해 주세요.'));
      return;
    }
    if (targetType === 'INDIVIDUAL' && !selectedRecipient) {
      alert(t('발송할 유저를 선택해 주세요.'));
      return;
    }
    if (targetType === 'INDIVIDUAL' && !selectedRecipient.email_verified) {
      alert(t('인증된 이메일이 없는 유저에게는 발송할 수 없습니다.'));
      return;
    }
    if (targetType === 'MARKETING_CONSENT' && recipientEstimate <= 0) {
      alert(t('마케팅 이메일 수신 동의 대상자가 없습니다.'));
      return;
    }

    const targetDescription = targetType === 'TEST'
      ? testEmail.trim()
      : targetType === 'INDIVIDUAL'
        ? `${selectedRecipient.nickname} (${selectedRecipient.user_code})`
        : t('마케팅 수신 동의 유저 {count}명', { count: formatNumber(recipientEstimate) });
    const confirmed = window.confirm(
      t('이메일을 발송할까요?\n\n대상: {target}\n제목: {subject}\n예상 발송: {count}건', { target: targetDescription, subject: form.subject.trim(), count: formatNumber(recipientEstimate) }),
    );
    if (!confirmed) return;

    setSending(true);
    try {
      const result = await createEmailCampaign({
        target_type: targetType,
        test_email: targetType === 'TEST' ? testEmail.trim() : undefined,
        target_user_code: targetType === 'INDIVIDUAL' ? selectedRecipient.user_code : undefined,
        ...form,
      });
      alert(result?.message || t('이메일이 발송 대기열에 등록되었습니다.'));
      await loadDashboard();
    } catch (error) {
      alert(error?.response?.data?.message || error.message || t('이메일 발송 요청에 실패했습니다.'));
    } finally {
      setSending(false);
    }
  };

  const openCampaign = async (campaignId) => {
    if (selectedCampaignId === campaignId) {
      setSelectedCampaignId(null);
      setDeliveries([]);
      return;
    }
    setSelectedCampaignId(campaignId);
    setDeliveryLoading(true);
    try {
      const result = await fetchEmailDeliveries(campaignId, { page: 1, page_size: 100 });
      setDeliveries(Array.isArray(result?.deliveries) ? result.deliveries : []);
    } catch (error) {
      alert(error?.response?.data?.message || error.message || t('개별 발송 이력을 불러오지 못했습니다.'));
      setDeliveries([]);
    } finally {
      setDeliveryLoading(false);
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-500">{t('이메일 관리 정보를 불러오는 중...')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('이메일 관리')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('support@nightpokerclub.com Microsoft 365 메일함 · 유저/마케팅 이메일 발송')}</p>
      </div>

      <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
        <button
          type="button"
          onClick={() => setViewMode('mailbox')}
          className={`rounded-lg px-5 py-2.5 text-sm font-bold transition ${viewMode === 'mailbox' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
        >
          {t('메일함')}
        </button>
        <button
          type="button"
          onClick={() => setViewMode('campaign')}
          className={`rounded-lg px-5 py-2.5 text-sm font-bold transition ${viewMode === 'campaign' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
        >
          {t('유저/마케팅 발송')}
        </button>
      </div>

      {viewMode === 'mailbox' ? (
        <MailboxPanel />
      ) : (
        <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label={t('인증 이메일 보유')} value={summary?.verified_recipient_count} note={t('개별 발송 가능')} />
        <StatCard label={t('마케팅 수신 동의')} value={summary?.marketing_recipient_count} note={t('전체 발송 대상')} />
        <StatCard label={t('현재 발송 대기')} value={summary?.queue_count} note={t('Redis 이메일 큐')} />
        <StatCard label={t('재시도 대기')} value={summary?.retry_count} note={t('SMTP 실패 재시도')} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)]">
        <section className="space-y-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('발송 대상')}</h2>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {Object.entries(TARGET_LABELS).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTargetType(value)}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold ${targetType === value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {t(label)}
                </button>
              ))}
            </div>
          </div>

          {targetType === 'TEST' ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{t('테스트 수신 이메일')}</label>
              <input
                type="email"
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                placeholder="admin@example.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
              />
            </div>
          ) : null}

          {targetType === 'INDIVIDUAL' ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('유저 검색')}</label>
              <div className="flex gap-2">
                <input
                  value={recipientQuery}
                  onChange={(event) => setRecipientQuery(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') handleRecipientSearch(); }}
                  placeholder={t('유저 코드, 아이디, 닉네임')}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                />
                <button type="button" onClick={handleRecipientSearch} disabled={searching} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {searching ? t('검색 중') : t('검색')}
                </button>
              </div>
              <div className="max-h-56 space-y-2 overflow-y-auto">
                {recipientResults.map((recipient) => (
                  <button
                    key={recipient.user_code}
                    type="button"
                    onClick={() => setSelectedRecipient(recipient)}
                    className={`w-full rounded-lg border p-3 text-left ${selectedRecipient?.user_code === recipient.user_code ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-900">{recipient.nickname} · {recipient.user_code}</div>
                        <div className="mt-1 truncate text-xs text-gray-500">{recipient.email_masked || t('등록 이메일 없음')}</div>
                      </div>
                      <div className="flex flex-shrink-0 flex-col items-end gap-1 text-[11px]">
                        <span className={recipient.email_verified ? 'text-emerald-600' : 'text-red-500'}>{recipient.email_verified ? t('인증됨') : t('미인증')}</span>
                        <span className={recipient.marketing_consent ? 'text-blue-600' : 'text-gray-400'}>{recipient.marketing_consent ? t('수신 동의') : t('수신 미동의')}</span>
                      </div>
                    </div>
                  </button>
                ))}
                {!searching && recipientQuery.trim() && recipientResults.length === 0 ? (
                  <div className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">{t('검색 결과가 없습니다.')}</div>
                ) : null}
              </div>
            </div>
          ) : null}

          {targetType === 'MARKETING_CONSENT' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              {t('인증된 이메일이 있고, 마케팅 이메일 수신에 동의했으며, 차단되지 않은 유저')} <strong>{formatNumber(summary?.marketing_recipient_count)}{t('명')}</strong>{t('에게 발송합니다.')}
            </div>
          ) : null}

          <div className="border-t border-gray-100 pt-5">
            <h2 className="text-lg font-semibold text-gray-900">{t('메일 작성')}</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('메일 제목')}</label>
                <input value={form.subject} maxLength={255} onChange={(event) => updateForm('subject', event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500" placeholder={t('받은편지함에 표시될 제목')} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('본문 제목')}</label>
                <input value={form.title} maxLength={255} onChange={(event) => updateForm('title', event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500" placeholder={t('비워두면 메일 제목 사용')} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('본문')}</label>
                <textarea value={form.body_text} maxLength={20000} onChange={(event) => updateForm('body_text', event.target.value)} rows={10} className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500" placeholder={t('발송할 내용을 입력하세요.')} />
                <div className="mt-1 text-right text-xs text-gray-400">{formatNumber(form.body_text.length)} / 20,000</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{t('버튼 문구')}</label>
                  <input value={form.button_label} maxLength={80} onChange={(event) => updateForm('button_label', event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500" placeholder={t('예: 사이트 바로가기')} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{t('버튼 URL')}</label>
                  <input value={form.button_url} onChange={(event) => updateForm('button_url', event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500" placeholder="https://..." />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('하단 안내')}</label>
                <textarea value={form.footer_text} maxLength={4000} onChange={(event) => updateForm('footer_text', event.target.value)} rows={3} className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500" />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-500">{t('예상 발송')} <strong className="text-gray-900">{formatNumber(recipientEstimate)}{t('건')}</strong></div>
            <button type="button" onClick={handleSend} disabled={sending} className="rounded-lg bg-blue-600 px-6 py-3 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
              {sending ? t('대기열 등록 중...') : t('이메일 발송')}
            </button>
          </div>
        </section>

        <section className="min-w-0 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('발송 미리보기')}</h2>
            <span className="text-xs text-gray-400">{t('실제 워커 템플릿 기준')}</span>
          </div>
          <EmailPreview form={form} />
        </section>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 p-4 sm:p-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('발송 이력')}</h2>
            <p className="mt-1 text-xs text-gray-500">{t('항목을 누르면 개별 발송 결과를 확인할 수 있습니다.')}</p>
          </div>
          <button type="button" onClick={loadDashboard} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">{t('새로고침')}</button>
        </div>

        <div className="divide-y divide-gray-100">
          {campaigns.map((campaign) => (
            <div key={campaign.campaign_id}>
              <button type="button" onClick={() => openCampaign(campaign.campaign_id)} className="grid w-full gap-3 p-4 text-left hover:bg-gray-50 sm:grid-cols-[minmax(0,1fr)_150px_190px] sm:items-center sm:p-5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(campaign.status)}`}>{STATUS_LABELS[campaign.status] ? t(STATUS_LABELS[campaign.status]) : campaign.status}</span>
                    <span className="text-xs text-gray-400">{TARGET_LABELS[campaign.target_type] ? t(TARGET_LABELS[campaign.target_type]) : campaign.target_type}</span>
                  </div>
                  <div className="mt-2 truncate font-semibold text-gray-900">{campaign.subject}</div>
                  <div className="mt-1 text-xs text-gray-400">{formatDate(campaign.created_at)}</div>
                </div>
                <div className="text-sm text-gray-600">
                  <div>{t('대상')} {formatNumber(campaign.recipient_count)}</div>
                  <div className="mt-1 text-xs text-gray-400">{t('대기열')} {formatNumber(campaign.queued_count)}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-lg bg-emerald-50 p-2 text-emerald-700">{t('성공')} {formatNumber(campaign.sent_count)}</div>
                  <div className="rounded-lg bg-red-50 p-2 text-red-700">{t('실패')} {formatNumber(campaign.failed_count)}</div>
                </div>
              </button>

              {selectedCampaignId === campaign.campaign_id ? (
                <div className="border-t border-gray-100 bg-gray-50 p-4 sm:p-5">
                  {deliveryLoading ? (
                    <div className="py-5 text-center text-sm text-gray-500">{t('개별 발송 결과를 불러오는 중...')}</div>
                  ) : deliveries.length === 0 ? (
                    <div className="py-5 text-center text-sm text-gray-500">{t('개별 발송 기록이 없습니다.')}</div>
                  ) : (
                    <div className="max-h-80 overflow-auto rounded-lg border border-gray-200 bg-white">
                      <table className="admin-email-deliveries-table min-w-full text-sm">
                        <thead className="sticky top-0 bg-gray-100 text-left text-xs text-gray-500">
                          <tr>
                            <th className="px-3 py-2">{t('유저 코드')}</th>
                            <th className="px-3 py-2">{t('수신자')}</th>
                            <th className="px-3 py-2">{t('상태')}</th>
                            <th className="px-3 py-2">{t('시도')}</th>
                            <th className="px-3 py-2">{t('처리 시각 / 오류')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {deliveries.map((delivery) => (
                            <tr key={delivery.job_id} className="admin-email-mobile-row">
                              <td data-label={t('유저 코드')} className="whitespace-nowrap px-3 py-2">{delivery.user_code || '-'}</td>
                              <td data-label={t('수신자')} className="whitespace-nowrap px-3 py-2 text-gray-600">{delivery.recipient_masked}</td>
                              <td data-label={t('상태')} className="px-3 py-2"><span className={`rounded-full px-2 py-1 text-xs ${statusClass(delivery.status)}`}>{DELIVERY_STATUS_LABELS[delivery.status] ? t(DELIVERY_STATUS_LABELS[delivery.status]) : delivery.status}</span></td>
                              <td data-label={t('시도')} className="px-3 py-2">{delivery.attempts}</td>
                              <td data-label={t('처리 시각 / 오류')} className="max-w-md px-3 py-2 text-xs text-gray-500">
                                <div>{formatDate(delivery.sent_at || delivery.failed_at || delivery.updated_at)}</div>
                                {delivery.last_error ? <div className="mt-1 break-words text-red-500">{delivery.last_error}</div> : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
          {campaigns.length === 0 ? <div className="p-10 text-center text-sm text-gray-500">{t('아직 발송 이력이 없습니다.')}</div> : null}
        </div>
      </section>
        </>
      )}
    </div>
  );
}
